import { Box, Typography } from '@mui/material';

import { DateFormatPatterns, Timezone } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatDate, getDateTimeInTimezone, parseNullableValue } from '@clearverify/utils';

import { VerificationRequestForReportItemFragment } from 'src/api';
import { Tooltip } from 'src/components/common';
import { DemoPrivacyAvatar, DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { InboxItemStatusChip } from 'src/components/inboxItem';
import { TableColumn } from 'src/components/table';
import { SxProp } from 'src/types/mui';
import { getVerificationTypeLabel } from 'src/utils/verificationRequest';

export const VERIFICATION_REQUESTS_TABLE_CONSUMER_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Employee',
    key: 'employee.createdAt',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verificationRequestItem) => {
      return (
        <Box sx={userWrapperStyles}>
          <DemoPrivacyAvatar
            src={verificationRequestItem.employeeAvatar?.downloadUrl}
            variant="circular"
          />
          <Typography variant="body2" ml={1}>
            <DemoPrivacyText>
              {parseNullableValue(verificationRequestItem.employeeFullName)}
            </DemoPrivacyText>
          </Typography>
        </Box>
      );
    },
  };

export const VERIFICATION_REQUESTS_TABLE_VERIFIER_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Verifier',
    key: 'verifierStaff.verifier.displayName',
    sortable: true,
    render: (_valueByDataPath, verificationRequestItem) => {
      return (
        <DemoPrivacyText>
          {parseNullableValue(verificationRequestItem.verifierDisplayName)}
        </DemoPrivacyText>
      );
    },
  };

export const VERIFICATION_REQUESTS_TABLE_VERIFIER_STAFF_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Verifier Staff',
    key: 'verifierStaff.user.firstName',
    sortable: true,
    render: (_valueByDataPath, verificationRequestItem) => {
      return parseNullableValue(verificationRequestItem.verifierStaffFullName);
    },
  };

export const VERIFICATION_REQUESTS_TABLE_EMPLOYERS_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Employers',
    key: 'employers',
    render: (_valueByDataPath, verificationRequestItem) => {
      return parseNullableValue(verificationRequestItem.employers);
    },
  };

export const VERIFICATION_REQUESTS_TABLE_PEO_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: "PEO's",
    key: 'peoDisplayName',
    render: (_valueByDataPath, verificationRequestItem) => {
      return parseNullableValue(verificationRequestItem.peos);
    },
  };

export const VERIFICATION_REQUESTS_TABLE_PERMISSIBLE_PURPOSE_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Permissible Purpose',
    key: 'permissiblePurpose.permissiblePurposeReference.label',
    sortable: true,
    render: (_valueByDataPath, verificationRequestItem) => {
      return parseNullableValue(verificationRequestItem.permissiblePurposeLabel);
    },
  };

export const VERIFICATION_REQUESTS_TABLE_CREATION_DATE_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Creation Date',
    key: 'createdAt',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verificationRequestItem) => {
      return formatDate(verificationRequestItem.createdAt, DateFormatPatterns.shortDateWithSlash);
    },
  };

export const VERIFICATION_REQUESTS_TABLE_TYPE_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Type',
    key: 'type',
    sortable: true,
    render: (_valueByDataPath, verificationRequestItem) => {
      return (
        <Tooltip title={verificationRequestItem.type}>
          <Typography variant="body2">
            {getVerificationTypeLabel(verificationRequestItem.type)}
          </Typography>
        </Tooltip>
      );
    },
  };

export const VERIFICATION_REQUESTS_TABLE_STATUS_COLUMN: TableColumn<VerificationRequestForReportItemFragment> =
  {
    title: 'Status',
    key: 'status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verificationRequestItem) => {
      return <InboxItemStatusChip status={verificationRequestItem.status} isVerificationRequest />;
    },
  };

const userWrapperStyles: SxProp = {
  display: 'flex',
  alignItems: 'center',
};

export const VERIFICATION_REQUESTS_TABLE_COLUMNS_CV_STAFF = [
  VERIFICATION_REQUESTS_TABLE_CONSUMER_COLUMN,
  VERIFICATION_REQUESTS_TABLE_VERIFIER_COLUMN,
  VERIFICATION_REQUESTS_TABLE_PERMISSIBLE_PURPOSE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_CREATION_DATE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_TYPE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_STATUS_COLUMN,
];

export const VERIFICATION_REQUESTS_TABLE_COLUMNS_VERIFIER_ADMIN = [
  VERIFICATION_REQUESTS_TABLE_CONSUMER_COLUMN,
  VERIFICATION_REQUESTS_TABLE_VERIFIER_STAFF_COLUMN,
  VERIFICATION_REQUESTS_TABLE_PERMISSIBLE_PURPOSE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_CREATION_DATE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_TYPE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_STATUS_COLUMN,
];

export const VERIFICATION_REQUESTS_TABLE_COLUMNS_VERIFIER_MANAGER = [
  VERIFICATION_REQUESTS_TABLE_CONSUMER_COLUMN,
  VERIFICATION_REQUESTS_TABLE_PERMISSIBLE_PURPOSE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_CREATION_DATE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_TYPE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_STATUS_COLUMN,
];

export const VERIFICATION_REQUESTS_TABLE_COLUMNS_EMPLOYER_STAFF: Array<
  TableColumn<VerificationRequestForReportItemFragment>
> = [
  VERIFICATION_REQUESTS_TABLE_CONSUMER_COLUMN,
  VERIFICATION_REQUESTS_TABLE_CREATION_DATE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_TYPE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_STATUS_COLUMN,
];

export const VERIFICATION_REQUESTS_TABLE_COLUMNS_PEO_STAFF: Array<
  TableColumn<VerificationRequestForReportItemFragment>
> = [
  VERIFICATION_REQUESTS_TABLE_CONSUMER_COLUMN,
  VERIFICATION_REQUESTS_TABLE_EMPLOYERS_COLUMN,
  VERIFICATION_REQUESTS_TABLE_CREATION_DATE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_TYPE_COLUMN,
  VERIFICATION_REQUESTS_TABLE_STATUS_COLUMN,
];

export const getVerificationRequestReportTableColumnsPeoPayrollProviderStaff = (
  timezone: Nullable<Timezone>,
) => [
  VERIFICATION_REQUESTS_TABLE_CONSUMER_COLUMN,
  VERIFICATION_REQUESTS_TABLE_PEO_COLUMN,
  VERIFICATION_REQUESTS_TABLE_EMPLOYERS_COLUMN,
  VERIFICATION_REQUESTS_TABLE_TYPE_COLUMN,
  getVerificationRequestReportTableCreationDateColumn(timezone),
  VERIFICATION_REQUESTS_TABLE_STATUS_COLUMN,
];

const getVerificationRequestReportTableCreationDateColumn = (
  timezone: Nullable<Timezone>,
): TableColumn<VerificationRequestForReportItemFragment> => ({
  title: 'Creation Date',
  key: 'createdAt',
  sortable: true,
  nonNullSortable: true,
  render: (_valueByDataPath, verificationRequest) => {
    return formatDate(
      getDateTimeInTimezone(verificationRequest.createdAt, timezone)?.toISO(),
      DateFormatPatterns.shortDateWithSlash,
    );
  },
});
